// frontend/src/contexts/SyncContext.jsx
// Global sync state — manages SSE connection, badge counts, and event callbacks
// IMPORTANT: Does NOT auto-sync on mount. Only syncs when triggerSync() is called.

import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { TokenStorage } from '../utils/api';
import api from '../utils/api';

const SyncContext = createContext(null);

export function useSync() {
  const ctx = useContext(SyncContext);
  if (!ctx) throw new Error('useSync must be used within SyncProvider');
  return ctx;
}

export function SyncProvider({ children }) {
  // --- Core sync state ---
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState('');
  const [syncStep, setSyncStep] = useState(0);
  const [lastSyncResult, setLastSyncResult] = useState(null);
  const [lastSyncAt, setLastSyncAt] = useState(null);

  // --- Badge counts ---
  const [pendingMatchCount, setPendingMatchCount] = useState(0);
  const [pendingSuggestionCount, setPendingSuggestionCount] = useState(0);

  // --- Refs for SSE management ---
  const abortRef = useRef(null);
  const syncingRef = useRef(false); // guards against double-sync

  // --- Ref-based callbacks for consumers (MatchingPanel subscribes here) ---
  const onStageRef = useRef(null);       // (data: { step, label, detail }) => void
  const onMatchRef = useRef(null);       // (data: { suggestion }) => void
  const onAutoMatchRef = useRef(null);   // (data: { item }) => void
  const onLogRef = useRef(null);         // (data: { message }) => void
  const onRecurringRef = useRef(null);   // (data: { newCount, totalPending, patterns }) => void
  const onCompleteRef = useRef(null);    // (data: { summary }) => void
  const onErrorRef = useRef(null);       // (data: { message }) => void

  // --- Fetch badge counts ---
  const refreshBadgeCounts = useCallback(async () => {
    try {
      const [matchRes, suggestionRes] = await Promise.all([
        api.get('/api/planner/suggested-matches?status=pending', { _backgroundRequest: true }).catch(() => ({ data: { suggestions: [] } })),
        api.get('/api/planner/recurring-suggestions/count', { _backgroundRequest: true }).catch(() => ({ data: { count: 0 } }))
      ]);
      setPendingMatchCount(matchRes.data.suggestions?.length || 0);
      if (suggestionRes.data.success) {
        setPendingSuggestionCount(suggestionRes.data.count || 0);
      }
    } catch {
      // Silently ignore — badges just won't show
    }
  }, []);

  // Fetch badge counts on mount (after a short delay to let auth settle)
  useEffect(() => {
    const timer = setTimeout(() => {
      refreshBadgeCounts();
    }, 1500);
    return () => clearTimeout(timer);
  }, [refreshBadgeCounts]);

  // --- Trigger full sync pipeline via SSE ---
  const triggerSync = useCallback(async () => {
    if (syncingRef.current) return; // guard against double-sync

    const token = TokenStorage.getToken();
    if (!token) {
      setSyncStatus('Auth required — please refresh.');
      return;
    }

    syncingRef.current = true;
    setSyncing(true);
    setSyncStatus('Connecting...');
    setSyncStep(0);
    setLastSyncResult(null);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      // Build SSE URL using the same base as Axios (handles localhost, proxy, and production)
      const base = (api.defaults.baseURL || '').replace(/\/+$/, '');
      const response = await fetch(`${base}/api/planner/sync-balance-stream`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'text/event-stream' },
        signal: controller.signal
      });

      if (!response.ok) throw new Error(`Server error: ${response.status}`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let result = { matchesFound: 0, needsReview: 0, newRecurring: 0, accountsSynced: 0, autoReconciled: [] };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let currentEvent = null;
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith('data: ') && currentEvent) {
            try {
              const data = JSON.parse(line.slice(6));

              switch (currentEvent) {
                case 'stage':
                  setSyncStatus(data.label || `Step ${data.step}...`);
                  setSyncStep(data.step || 0);
                  if (onStageRef.current) onStageRef.current(data);
                  break;
                case 'log':
                  if (onLogRef.current) onLogRef.current(data);
                  break;
                case 'match':
                  if (onMatchRef.current) onMatchRef.current(data);
                  break;
                case 'auto_match':
                  if (onAutoMatchRef.current) onAutoMatchRef.current(data);
                  break;
                case 'skeletons':
                  // Forward to consumers who care (MatchingPanel)
                  if (onStageRef.current) onStageRef.current({ ...data, type: 'skeletons' });
                  break;
                case 'recurring':
                  result.newRecurring = data.newCount || 0;
                  if (onRecurringRef.current) onRecurringRef.current(data);
                  break;
                case 'complete':
                  result.matchesFound = data.summary?.matchesFound || 0;
                  result.needsReview = data.summary?.needsReview || 0;
                  result.accountsSynced = data.summary?.accountsSynced || 0;
                  result.autoReconciled = data.summary?.autoReconciled || [];
                  if (onCompleteRef.current) onCompleteRef.current(data);
                  break;
                case 'error':
                  setSyncStatus(`Error: ${data.message}`);
                  if (onErrorRef.current) onErrorRef.current(data);
                  break;
                default:
                  break;
              }
            } catch { /* ignore parse errors */ }
            currentEvent = null;
          }
        }
      }

      // Sync completed successfully
      setLastSyncResult(result);
      setLastSyncAt(new Date());
      setSyncStatus('');
      setSyncStep(0);

      // Refresh badge counts after sync
      await refreshBadgeCounts();

    } catch (err) {
      if (err.name !== 'AbortError') {
        setSyncStatus(`Sync failed: ${err.message}`);
        if (onErrorRef.current) onErrorRef.current({ message: err.message });
      }
    } finally {
      setSyncing(false);
      syncingRef.current = false;
      abortRef.current = null;
    }
  }, [refreshBadgeCounts]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, []);

  const value = {
    // State
    syncing,
    syncStatus,
    syncStep,
    lastSyncResult,
    lastSyncAt,
    pendingMatchCount,
    pendingSuggestionCount,

    // Methods
    triggerSync,
    refreshBadgeCounts,

    // Ref-based callbacks for consumers to subscribe
    onStageRef,
    onMatchRef,
    onAutoMatchRef,
    onLogRef,
    onRecurringRef,
    onCompleteRef,
    onErrorRef,
  };

  return (
    <SyncContext.Provider value={value}>
      {children}
    </SyncContext.Provider>
  );
}
